import { GLOBAL_QUERY_MAX, GLOBAL_QUERY_MIN, type GlobalSearchResponse } from '@hoopoe/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from './api';

// Global search (⌘K, task 23): entities and Library documents from /api/search. Help articles are searched in the
// browser (lib/help/search.ts) and merged by the palette.

/** Wait after the last keystroke before asking the server (search targets < 300 ms p95, PLAN §13.3). */
export const SEARCH_DEBOUNCE_MS = 150;

export const normalizeQuery = (q: string) => q.trim().slice(0, GLOBAL_QUERY_MAX);
export const isSearchable = (q: string) => [...normalizeQuery(q)].length >= GLOBAL_QUERY_MIN;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Matches the meaning half may add per kind of entity, and to the Library group, under what text found. */
export const MEANING_EXTRA = 3;

/** Global search results as shown: the text half, then what the meaning half adds (`added`: ids it added). */
export type ShownSearch = GlobalSearchResponse & { added: ReadonlySet<string> };

/**
 * Progressive search (D388): the text half's results in their order, then the meaning half's entities and
 * documents that text didn't find (at most MEANING_EXTRA per kind and for the Library), so nothing on screen moves
 * when they arrive. Halves for different queries are never mixed.
 */
export function mergeSearch(text: GlobalSearchResponse | undefined, meaning: GlobalSearchResponse | undefined): ShownSearch | undefined {
  if (!text)
    return meaning && { ...meaning, added: new Set([...meaning.entities.map(e => e.id), ...meaning.documents.map(d => d.documentId)]) };
  if (!meaning || meaning.q !== text.q) return { ...text, added: new Set() };
  const shown = new Set(text.entities.map(e => e.id));
  const perKind = new Map<string, number>();
  const entities = meaning.entities.filter(e => {
    if (shown.has(e.id)) return false;
    const n = perKind.get(e.kind) ?? 0;
    perKind.set(e.kind, n + 1);
    return n < MEANING_EXTRA;
  });
  const docs = new Set(text.documents.map(d => d.documentId));
  const documents = meaning.documents.filter(d => !docs.has(d.documentId)).slice(0, MEANING_EXTRA);
  return {
    ...text,
    part: 'all',
    entities: [...text.entities, ...entities],
    more: [...new Set([...text.more, ...meaning.more])],
    documents: [...text.documents, ...documents],
    moreDocuments: text.moreDocuments || meaning.moreDocuments,
    vectors: meaning.vectors,
    added: new Set([...entities.map(e => e.id), ...documents.map(d => d.documentId)]),
  };
}

/**
 * Server results for a query, debounced, in two halves asked side by side (progressive search, D388): text
 * (codes, names, words) answers at once; meaning (names in other scripts and spellings, similar passages) is added
 * when the AI has embedded the query. While a new query runs the previous results stay on screen (no flicker);
 * `pending` is true until the text results for what is typed now have arrived, `addingMeaning` while the meaning
 * half for it is still on its way.
 */
export function useGlobalSearch(q: string) {
  const typed = normalizeQuery(q);
  const debounced = useDebounced(typed, SEARCH_DEBOUNCE_MS);
  const enabled = isSearchable(debounced);
  const half = (part: 'text' | 'meaning') => ({
    queryKey: ['search', part, debounced],
    queryFn: () => api<GlobalSearchResponse>(`/search?q=${encodeURIComponent(debounced)}&part=${part}`),
    enabled,
    staleTime: 10_000,
    placeholderData: (prev: GlobalSearchResponse | undefined) => prev,
  });
  const text = useQuery(half('text'));
  const meaning = useQuery(half('meaning'));
  const searchable = isSearchable(typed);
  const data = searchable && enabled ? mergeSearch(text.data, meaning.data) : undefined;
  const pending = searchable && (debounced !== typed || text.isFetching || text.data?.q !== typed);
  const addingMeaning =
    searchable && !pending && !!text.data?.vectors && !meaning.isError && (meaning.isFetching || meaning.data?.q !== typed);
  return { data, pending, addingMeaning, error: text.isError };
}
